import { Component, signal, input, output, OnInit } from '@angular/core';
import { IDataClassConfig } from 'app/classes/data-table/data-table';
import { Issue, IssueVisibility } from 'app/classes/issue';
import { DataForm } from 'app/layout/data-form/data-form.component';
import { APIService } from 'app/shared/api.service';
import { AssetService } from 'app/shared/asset.service';
import { DateService } from 'app/shared/date.service';
import { IssueTrackingStatusType, IssueTrackingService, IssueTrackingProgressType } from 'app/shared/issue-tracking.service';
import { User } from 'app/classes/user';
import { IDatesFromTo } from 'app/interfaces/dates-from-to';

@Component({
  selector: 'app-issue-tracking-update',
  standalone: false,
  templateUrl: './issue-tracking-update.component.html',
  styleUrl: './issue-tracking-update.component.css'
})
export class IssueTrackingUpdateComponent implements OnInit {

  issueId = input.required<number>();
  issue = signal<Issue>(null);
  editForm = signal<{ substatus_id: number; description: string, title: string, rag: string, taskProgress: IssueTrackingProgressType, visibility: IssueVisibility, assignee: number, status: IssueTrackingStatusType, completedAt: string, resolvedAt: string }>(null);
  completeEditForm = signal<{ substatus_id: number, completedNotes: string, resolvedAt: string }>(null);
  dialogConfirm = signal<'delete'>(null);
  isUpdating = signal<boolean>(false);
  wantsToComplete = signal<boolean>(false);

  onUpdated = output<Issue>();
  onCompleted = output<{ substatus_id: number, completedNotes: string }>();

  isDragging = false;
  hasDropped = false;
  chartRange: IDatesFromTo;
  orgUsers: User[];
  statusOptions: { id: number, title: string }[];
  isMobile = false;
  user: User;
  can: { edit: boolean } = { edit: false };
  dataForm: DataForm;

  statusOptionsForComplete: { id: number, title: string }[];



  constructor(private issueTrackingService: IssueTrackingService, private assetService: AssetService, private apiService: APIService) {
    this.issueTrackingService.getAllowedAssignees().then(r => this.orgUsers = r);
    this.can.edit = this.apiService.isAdmin();
    this.user = this.apiService.getThisUser();
    this.statusOptions = issueTrackingService.getOptionsForStatusBeforeComplete();
    this.statusOptionsForComplete = issueTrackingService.getOptionsForStatusWhenCompleting();
  }

  ngOnInit(): void {
    const config: IDataClassConfig = {};
    this.dataForm = new DataForm({ data: this.issue() },);
    const id = this.issueId();
    this.getIssue(id);
  }

  getIssue(id: number) {
    this.issueTrackingService.getIssue(id).then((issue) => {

      this.issue.set(issue);
      this.editForm.set({
        description: this.issue().description,
        rag: this.issue().rag,
        taskProgress: this.issue().taskProgress,
        visibility: this.issue().visibility,
        assignee: this.issue().assignee ? +this.issue().assignee : null,
        status: this.issue().status,
        title: this.issue().title,
        substatus_id: this.issue().substatus_id,
        completedAt: this.issue().completedAt ? this.issue().completedAt.toISOString().split('T')[0] : null,
        resolvedAt: this.issue().resolvedAt ? this.issue().resolvedAt.toISOString().split('T')[0] : null,
      });

      if (this.apiService.getUserId() === issue.assignee) {
        this.can.edit = true;
      }
    });
  }

  setRag(rag: string) {
    this.editForm.update(issue => {
      issue.rag = rag;
      return issue;
    });
  }

  saveEdit() {
    const payload: any = {};
    if (this.editForm().title !== this.issue().title) {
      payload.title = this.editForm().title;
    }

    if (this.editForm().visibility !== this.issue().visibility) {
      payload.visibility = this.editForm().visibility;
    }

    if (this.editForm().description !== this.issue().description) {
      payload.description = this.editForm().description;
    }

    if (this.editForm().rag !== this.issue().rag) {
      payload.rag = this.editForm().rag;
    }

    if (this.editForm().taskProgress !== this.issue().taskProgress) {
      payload.taskProgress = this.editForm().taskProgress;
    }

    if (this.editForm().assignee !== this.issue().assignee) {
      payload.assignee = this.editForm().assignee;
    }

    if (this.editForm().status !== this.issue().status) {
      payload.status = this.editForm().status;
    }

    if (this.editForm().substatus_id !== this.issue().substatus_id) {
      payload.substatus_id = this.editForm().substatus_id;
    }

    if (this.editForm().completedAt !== this.issue().completedAt?.toISOString().split('T')[0]) {
      payload.completedAt = DateService.dbDate(this.editForm().completedAt);
    }

    if (this.editForm().resolvedAt !== this.issue().resolvedAt?.toISOString().split('T')[0]) {
      payload.resolvedAt = DateService.dbDate(this.editForm().resolvedAt);
    }


    if (payload.resolvedAt && this.issue().substatus_id < 6) {
      this.apiService.toastWarn('Cannot resolve issue if open.');

      return;
    }


    if (Object.keys(payload).length === 0) {
      this.apiService.toastWarn('nothing changed.');
      this.onUpdated.emit(null);

      return;
    };
    this.isUpdating.set(true);
    this.apiService.toastSuccess('saving...', null, 500);

    this.issueTrackingService.updateIssue(this.issue().id, payload).then(() => {
      this.getIssue(this.issue().id);
      this.isUpdating.set(false);
      setTimeout(() => {
        this.apiService.toastSuccess('saved.');
        this.onUpdated.emit(null);
      }, 500);

    });
  }
  wantsToCompleteClick() {
    this.wantsToComplete.set(true);
    this.completeEditForm.set({ substatus_id: 6, completedNotes: '', resolvedAt: new Date().toISOString().split('T')[0] });
  }

  completeIssue() {
    if (!this.completeEditForm().completedNotes) {
      this.apiService.toastWarn('Please enter some notes before saving.');
      return;
    }
    const payload: any = {};
    payload.substatus_id = this.completeEditForm().substatus_id;
    payload.completedNotes = this.completeEditForm().completedNotes;
    payload.resolvedAt = DateService.dbDate(this.completeEditForm().resolvedAt);
    this.issueTrackingService.completeIssue(this.issue().id, payload).then(() => {
      this.wantsToComplete.set(false);
      this.onCompleted.emit(this.completeEditForm());
    });
  }

  deleteClick() {
    this.apiService.toastSuccess('Deleting issue...');
    this.issueTrackingService.deleteIssue(this.issue().id).then(() => {

      setTimeout(() => {
        this.dialogConfirm.set(null);
        this.onUpdated.emit(null);
        this.apiService.toastSuccess('deleted.');
      }, 100);
    });
  }
}
